<template>
	<div
		class="inline-video-player"
		:class="[
			`inline-video-player--${variant}`,
			{
				'inline-video-player--ready': hasSource,
				'inline-video-player--disabled': disabled
			}
		]"
		:style="shellStyle"
	>
		<div class="inline-video-player__stage" :style="stageStyle" @click.stop="onStageClick">
			<img
				v-if="!hasSource && poster"
				class="inline-video-player__poster"
				:src="poster"
				alt="视频封面"
				@error="$emit('poster-error')"
			/>
			<div v-else-if="!hasSource" class="inline-video-player__poster inline-video-player__poster--empty"></div>
			<video
				v-else
				ref="player"
				class="inline-video-player__media"
				:src="source"
				:poster="poster"
				playsinline
				preload="metadata"
				controlslist="nodownload noplaybackrate"
				disablepictureinpicture
				@loadedmetadata="onLoadedMetadata"
				@timeupdate="onTimeUpdate"
				@play="onNativePlay"
				@pause="onNativePause"
				@ended="onNativeEnded"
				@error="onNativeError"
			></video>
			<div
				v-if="transferState"
				class="inline-video-player__transfer"
				:class="{ 'inline-video-player__transfer--failed': !transferState.progress }"
			>
				<div class="inline-video-player__transfer-body">
					<span class="inline-video-player__transfer-label">{{ transferState.text }}</span>
				</div>
				<div v-if="transferState.progress" class="inline-video-player__transfer-bar">
					<span></span>
				</div>
			</div>
			<div
				v-else-if="showOverlay"
				class="inline-video-player__overlay"
				:class="{
					'inline-video-player__overlay--loading': loading,
					'inline-video-player__overlay--error': loadError
				}"
			>
				<div class="inline-video-player__overlay-inner" :class="{ 'inline-video-player__overlay-inner--stacked': Boolean(overlayText) }">
					<i :class="overlayIcon"></i>
					<span v-if="overlayText">{{ overlayText }}</span>
				</div>
			</div>
			<button
				v-if="previewable && !transferState"
				type="button"
				class="inline-video-player__preview-btn"
				title="放大预览"
				@click.stop="onRequestPreview"
			>
				<i class="el-icon-full-screen"></i>
			</button>
		</div>
		<div
			class="inline-video-player__controls"
			:class="{ 'inline-video-player__controls--compact': compactControls }"
		>
			<button
				type="button"
				class="inline-video-player__toggle"
				:class="{ 'inline-video-player__toggle--disabled': disabled || loading }"
				:title="playing ? '暂停视频' : '播放视频'"
				@click.stop="togglePlayback"
			>
				<span class="inline-video-player__toggle-icon" :class="{ 'inline-video-player__toggle-icon--playing': playing }"></span>
			</button>
			<div class="inline-video-player__timeline" @click.stop="onSeek">
				<span class="inline-video-player__timeline-track"></span>
				<span class="inline-video-player__timeline-value" :style="{ width: `${progressPercent}%` }"></span>
			</div>
			<span class="inline-video-player__time">{{ compactControlTimeText }}</span>
		</div>
	</div>
</template>

<script>
export default {
	name: "inlineVideoPlayer",
	props: {
		source: {
			type: String,
			default: ''
		},
		poster: {
			type: String,
			default: ''
		},
		loading: {
			type: Boolean,
			default: false
		},
		loadError: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		},
		duration: {
			type: [Number, String],
			default: 0
		},
		contentWidth: {
			type: [Number, String],
			default: 0
		},
		contentHeight: {
			type: [Number, String],
			default: 0
		},
		maxWidth: {
			type: Number,
			default: 360
		},
		maxHeight: {
			type: Number,
			default: 360
		},
		minWidth: {
			type: Number,
			default: 180
		},
		fallbackAspectRatio: {
			type: String,
			default: '16 / 9'
		},
		variant: {
			type: String,
			default: 'message'
		},
		placeholderText: {
			type: String,
			default: '点击播放视频'
		},
		transferState: {
			type: Object,
			default: null
		},
		previewable: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			playing: false,
			currentTime: 0,
			metadataDuration: 0,
			autoPlayPending: false
		};
	},
	computed: {
		hasSource() {
			return Boolean(this.source);
		},
		stageSize() {
			const width = Number(this.contentWidth) || 0;
			const height = Number(this.contentHeight) || 0;
			if (!width || !height) {
				return this.buildFallbackStageSize();
			}
			const scale = Math.min(1, this.maxWidth / width, this.maxHeight / height);
			return {
				width: Math.max(1, Math.round(width * scale)),
				height: Math.max(1, Math.round(height * scale))
			};
		},
		shellStyle() {
			return {
				width: `${this.stageSize.width}px`,
				maxWidth: '100%'
			};
		},
		stageStyle() {
			return {
				width: `${this.stageSize.width}px`,
				height: `${this.stageSize.height}px`
			};
		},
		resolvedDuration() {
			const duration = this.metadataDuration || Number(this.duration) || 0;
			return Number.isFinite(duration) && duration > 0 ? duration : 0;
		},
		progressPercent() {
			if (!this.resolvedDuration) {
				return 0;
			}
			return Math.min(100, Math.max(0, this.currentTime / this.resolvedDuration * 100));
		},
		controlTimeText() {
			return `${this.formatTime(this.currentTime)} / ${this.formatTime(this.resolvedDuration)}`;
		},
		compactControlTimeText() {
			return `${this.formatTime(this.currentTime)}/${this.formatTime(this.resolvedDuration)}`;
		},
		compactControls() {
			return this.stageSize.width <= 120;
		},
		showOverlay() {
			if (this.transferState) {
				return false;
			}
			if (!this.hasSource) {
				return true;
			}
			return !this.playing;
		},
		overlayText() {
			if (this.loading) {
				return '视频加载中';
			}
			if (this.loadError) {
				return '视频暂不可用';
			}
			if (!this.hasSource) {
				return this.placeholderText;
			}
			return '';
		},
		overlayIcon() {
			if (this.loading) {
				return 'el-icon-loading';
			}
			if (this.loadError) {
				return 'el-icon-video-camera';
			}
			return 'el-icon-video-play';
		}
	},
	watch: {
		source: {
			immediate: true,
			handler(nextSource, prevSource) {
				if (nextSource === prevSource) {
					return;
				}
				this.resetPlaybackState();
				if (nextSource) {
					this.autoPlayPending = true;
					this.$nextTick(() => {
						this.tryAutoPlay();
					});
				}
			}
		}
	},
	methods: {
		parseAspectRatio() {
			const matched = String(this.fallbackAspectRatio || '16 / 9').match(/(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/);
			if (!matched) {
				return 16 / 9;
			}
			const width = Number(matched[1]) || 16;
			const height = Number(matched[2]) || 9;
			if (!width || !height) {
				return 16 / 9;
			}
			return width / height;
		},
		buildFallbackStageSize() {
			const ratio = this.parseAspectRatio();
			const baseWidth = Math.min(this.maxWidth, Math.max(this.minWidth, Math.round(this.maxWidth * 0.72)));
			const baseHeight = Math.min(this.maxHeight, Math.max(96, Math.round(baseWidth / ratio)));
			return {
				width: baseWidth,
				height: baseHeight
			};
		},
		formatTime(seconds) {
			if (!Number.isFinite(seconds) || seconds <= 0) {
				return '00:00';
			}
			const totalSeconds = Math.max(0, Math.floor(seconds));
			const minutes = Math.floor(totalSeconds / 60);
			const remainSeconds = totalSeconds % 60;
			return `${String(minutes).padStart(2, '0')}:${String(remainSeconds).padStart(2, '0')}`;
		},
		onStageClick() {
			if (this.disabled || this.transferState) {
				return;
			}
			if (!this.hasSource) {
				this.$emit('request-load');
				return;
			}
			this.togglePlayback();
		},
		onRequestPreview() {
			if (this.disabled || this.transferState || !this.previewable) {
				return;
			}
			const player = this.$refs.player;
			if (player && !player.paused) {
				player.pause();
			}
			this.$emit('request-preview');
		},
		togglePlayback() {
			if (!this.hasSource || this.loading || this.disabled) {
				if (!this.hasSource) {
					this.$emit('request-load');
				}
				return;
			}
			const player = this.$refs.player;
			if (!player) {
				return;
			}
			if (player.paused) {
				player.play().catch(() => {});
				return;
			}
			player.pause();
		},
		tryAutoPlay() {
			if (!this.autoPlayPending || !this.hasSource) {
				return;
			}
			const player = this.$refs.player;
			if (!player) {
				return;
			}
			player.play().then(() => {
				this.autoPlayPending = false;
			}).catch(() => {});
		},
		onLoadedMetadata(event) {
			this.metadataDuration = Number(event && event.target && event.target.duration) || 0;
			this.tryAutoPlay();
		},
		onTimeUpdate(event) {
			this.currentTime = Number(event && event.target && event.target.currentTime) || 0;
		},
		onNativePlay() {
			this.playing = true;
			this.autoPlayPending = false;
		},
		onNativePause(event) {
			this.playing = false;
			this.currentTime = Number(event && event.target && event.target.currentTime) || this.currentTime;
		},
		onNativeEnded(event) {
			this.playing = false;
			this.currentTime = Number(event && event.target && event.target.duration) || this.currentTime;
		},
		onNativeError() {
			this.resetPlaybackState();
			this.$emit('error');
		},
		onSeek(event) {
			if (!this.resolvedDuration) {
				return;
			}
			const player = this.$refs.player;
			if (!player) {
				return;
			}
			const rect = event.currentTarget.getBoundingClientRect();
			if (!rect.width) {
				return;
			}
			const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
			player.currentTime = this.resolvedDuration * ratio;
			this.currentTime = player.currentTime;
		},
		resetPlaybackState() {
			this.playing = false;
			this.currentTime = 0;
			this.metadataDuration = 0;
			this.autoPlayPending = false;
			const player = this.$refs.player;
			if (!player) {
				return;
			}
			player.pause();
		}
	},
	beforeDestroy() {
		this.resetPlaybackState();
	}
}
</script>

<style scoped lang="scss">
.inline-video-player {
	display: inline-flex;
	flex-direction: column;
	max-width: 100%;
	border: 1px solid var(--im-control-border);
	border-radius: 12px;
	background: rgba(var(--im-black-rgb), 0.02);
	overflow: hidden;
	box-sizing: border-box;

	&__stage {
		position: relative;
		max-width: 100%;
		background: rgba(var(--im-black-rgb), 0.94);
		overflow: hidden;
		cursor: pointer;
	}

	&__poster,
	&__media {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: contain;
		background: rgba(var(--im-black-rgb), 0.94);
	}

	&__poster--empty {
		background: linear-gradient(180deg, rgba(var(--im-black-rgb), 0.82), rgba(var(--im-black-rgb), 0.68));
	}

	&__overlay {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(var(--im-black-rgb), 0.12);
		color: rgba(var(--im-white-rgb), 0.96);
	}

	&__overlay--loading {
		background: rgba(var(--im-black-rgb), 0.28);
	}

	&__overlay--error {
		background: rgba(var(--im-black-rgb), 0.36);
	}

	&__overlay-inner {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 10px;
		padding: 0 16px;

		i {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: 42px;
			height: 42px;
			border-radius: 999px;
			background: rgba(var(--im-black-rgb), 0.48);
			border: 1px solid rgba(var(--im-white-rgb), 0.16);
			font-size: 18px;
		}

		span {
			font-size: 12px;
			line-height: 1.4;
			font-weight: 500;
		}
	}

	&__overlay-inner--stacked {
		flex-direction: column;
		gap: 8px;
	}

	&__preview-btn {
		position: absolute;
		top: 8px;
		right: 8px;
		z-index: 3;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 28px;
		height: 28px;
		padding: 0;
		border: 1px solid rgba(var(--im-white-rgb), 0.18);
		border-radius: 999px;
		background: rgba(var(--im-black-rgb), 0.38);
		color: rgba(var(--im-white-rgb), 0.94);
		cursor: pointer;
		transition: background-color 0.2s ease, border-color 0.2s ease;

		&:hover {
			background: rgba(var(--im-black-rgb), 0.54);
			border-color: rgba(var(--im-white-rgb), 0.3);
		}

		i {
			font-size: 13px;
		}
	}

	&__controls {
		display: grid;
		grid-template-columns: 24px minmax(0, 1fr);
		grid-template-areas:
			"toggle time"
			"timeline timeline";
		gap: 4px 8px;
		min-height: 42px;
		padding: 6px 8px;
		background: rgba(var(--im-black-rgb), 0.84);
		color: rgba(var(--im-white-rgb), 0.94);
		box-sizing: border-box;
	}

	&__controls--compact {
		grid-template-areas:
			"toggle timeline"
			"time time";
	}

	&__toggle {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 24px;
		height: 24px;
		padding: 0;
		border: none;
		background: transparent;
		color: inherit;
		cursor: pointer;
		grid-area: toggle;
		align-self: center;
	}

	&__toggle--disabled {
		opacity: 0.56;
	}

	&__toggle-icon {
		position: relative;
		display: inline-block;
		width: 12px;
		height: 12px;
		color: currentColor;

		&::before,
		&::after {
			content: '';
			position: absolute;
			display: block;
		}

		&::before {
			top: 0;
			left: 1px;
			width: 0;
			height: 0;
			border-style: solid;
			border-width: 6px 0 6px 10px;
			border-color: transparent transparent transparent currentColor;
		}

		&--playing {
			&::before,
			&::after {
				top: 0;
				width: 3px;
				height: 12px;
				border: none;
				border-radius: 999px;
				background: currentColor;
			}

			&::before {
				left: 1px;
			}

			&::after {
				right: 1px;
			}
		}
	}

	&__timeline {
		position: relative;
		display: flex;
		align-items: center;
		height: 16px;
		min-width: 18px;
		cursor: pointer;
		grid-area: timeline;
		width: 100%;
	}

	&__timeline-track,
	&__timeline-value {
		position: absolute;
		left: 0;
		top: 50%;
		height: 3px;
		border-radius: 999px;
		transform: translateY(-50%);
	}

	&__timeline-track {
		right: 0;
		background: rgba(var(--im-white-rgb), 0.22);
	}

	&__timeline-value {
		background: var(--im-color-primary);
	}

	&__time {
		font-size: 11px;
		line-height: 1;
		white-space: nowrap;
		font-variant-numeric: tabular-nums;
		color: rgba(var(--im-white-rgb), 0.86);
		grid-area: time;
		justify-self: end;
		align-self: center;
	}

	&__transfer {
		position: absolute;
		inset: 0;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 10px;
		padding: 18px 16px;
		background: rgba(var(--im-black-rgb), 0.42);
		color: rgba(var(--im-white-rgb), 0.96);
	}

	&__transfer--failed {
		background: rgba(var(--im-black-rgb), 0.54);
	}

	&__transfer-body {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		text-align: center;
	}

	&__transfer-label {
		font-size: 12px;
		line-height: 1.4;
		font-weight: 500;
	}

	&__transfer-bar {
		width: min(168px, 100%);
		height: 4px;
		border-radius: 999px;
		background: rgba(var(--im-white-rgb), 0.18);
		overflow: hidden;

		span {
			display: block;
			width: 40%;
			height: 100%;
			border-radius: inherit;
			background: rgba(var(--im-white-rgb), 0.92);
			animation: inline-video-player-transfer 1.1s ease-in-out infinite;
		}
	}

	&--history {
		border-radius: 10px;

		.inline-video-player__time {
			font-size: 10px;
		}

		.inline-video-player__overlay-inner i {
			width: 36px;
			height: 36px;
			font-size: 16px;
		}
	}

	&--disabled {
		.inline-video-player__stage {
			cursor: not-allowed;
		}
	}
}

@keyframes inline-video-player-transfer {
	0% {
		transform: translateX(-100%);
	}

	100% {
		transform: translateX(260%);
	}
}
</style>
